import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { http, formatErr } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", slug: "", description: "", short_description: "",
  price: 0, compare_at_price: null, currency: "INR",
  images: [], tags: [], category: "", stock: 100, sku: "",
  is_active: true, is_featured: false, badge: "",
};

export default function ProductEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [p, setP] = useState(empty);
  const [imgInput, setImgInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    http.get(`/admin/products`).then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) setP({ ...empty, ...found });
    });
  }, [id]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p };
      if (!payload.compare_at_price) payload.compare_at_price = null;
      if (isNew) {
        const { data } = await http.post("/admin/products", payload);
        toast.success("Product created");
        nav(`/admin/products/${data.id}`);
      } else {
        await http.put(`/admin/products/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (!imgInput.trim()) return;
    set("images", [...p.images, imgInput.trim()]);
    setImgInput("");
  };

  return (
    <div>
      <Link to="/admin/products" className="text-sm text-white/50 inline-flex items-center gap-1 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl font-bold">{isNew ? "New product" : p.name || "Edit product"}</h1>
        <button onClick={save} disabled={saving} className="bls-admin-btn" data-testid={TID.adminProductSave}>
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bls-admin-card p-6 space-y-4">
            <Field label="Name" value={p.name} onChange={(v) => set("name", v)} testid="admin-pname" />
            <Field label="Slug (URL)" value={p.slug} onChange={(v) => set("slug", v)} testid="admin-pslug" placeholder="auto-generated if empty" />
            <Field label="Short description" value={p.short_description} onChange={(v) => set("short_description", v)} testid="admin-pshort" />
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 font-medium">Description (HTML allowed)</label>
              <textarea
                value={p.description}
                onChange={(e) => set("description", e.target.value)}
                className="bls-admin-input mt-2 h-32"
                data-testid="admin-pdesc"
              />
            </div>
          </div>

          <div className="bls-admin-card p-6 space-y-4">
            <h3 className="font-display font-bold">Images</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {p.images.map((url, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#16161f]">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("images", p.images.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white text-xs">
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={imgInput}
                onChange={(e) => setImgInput(e.target.value)}
                placeholder="Image URL (https://…)"
                className="bls-admin-input flex-1"
                data-testid="admin-pimage-input"
              />
              <button onClick={addImage} className="bls-admin-btn-ghost"><Plus className="w-3 h-3" /> Add</button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bls-admin-card p-6 space-y-4">
            <h3 className="font-display font-bold">Pricing & inventory</h3>
            <Field label="Price (₹)" type="number" value={p.price} onChange={(v) => set("price", Number(v))} testid="admin-pprice" />
            <Field label="Compare-at price (₹)" type="number" value={p.compare_at_price || ""} onChange={(v) => set("compare_at_price", v ? Number(v) : null)} testid="admin-pcomp" />
            <Field label="Stock" type="number" value={p.stock} onChange={(v) => set("stock", Number(v))} testid="admin-pstock" />
            <Field label="SKU" value={p.sku || ""} onChange={(v) => set("sku", v)} testid="admin-psku" />
          </div>

          <div className="bls-admin-card p-6 space-y-4">
            <h3 className="font-display font-bold">Organization</h3>
            <Field label="Category" value={p.category || ""} onChange={(v) => set("category", v)} testid="admin-pcat" />
            <Field label="Badge (Bestseller / New / Sale)" value={p.badge || ""} onChange={(v) => set("badge", v)} testid="admin-pbadge" />
            <Toggle label="Active (visible on storefront)" checked={p.is_active} onChange={(v) => set("is_active", v)} testid="admin-pactive" />
            <Toggle label="Featured on homepage" checked={p.is_featured} onChange={(v) => set("is_featured", v)} testid="admin-pfeatured" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder = "" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50 font-medium">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bls-admin-input mt-2"
        data-testid={testid}
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-white/80">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? "bg-[#7f77dd]" : "bg-white/10"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}
